// État de la liste d'aliments d'une analyse, et tout le calcul qui va avec.
//
// Le modèle propose, l'utilisateur décide (README « Partis pris ») : une fois l'analyse rendue,
// bouger un curseur, renommer, supprimer ou ajouter un aliment ne redemande RIEN au serveur — les
// macros sont une règle de trois sur la composition pour 100 g, elle se fait ici, en local, à chaque
// pixel de curseur. Aucune fonction de ce fichier ne touche au DOM ni au réseau : tout est testé
// sous `node --test` (test/portions.test.ts).
import { scaleMacros } from '../barcode/scaleMacros.js';
import type { MacrosPer100g } from '../barcode/types.js';
import type { AlimentDetecte, PoidsEstime } from '../analyze/types.js';

/** En dessous, l'aliment est présenté comme « à vérifier » et invite à la correction. */
export const SEUIL_CONFIANCE_BASSE = 0.6;

export interface AlimentAjustable {
  id: string;
  nom: string;
  /** 0..1 tel que rendu par le modèle ; `null` pour un aliment ajouté à la main. */
  confiance: number | null;
  origine: 'modele' | 'manuel';
  /** Fourchette proposée par le modèle (`null` pour un ajout manuel). */
  estimation: PoidsEstime | null;
  /** Composition pour 100 g ; `null` = composition inconnue (ajout manuel « autre »). */
  macrosPour100g: MacrosPer100g | null;
  /** Poids retenu, en grammes — valeur du curseur. */
  poidsG: number;
  /** `true` dès que l'utilisateur a bougé le curseur : la correction vaut la peine d'être remontée. */
  ajuste: boolean;
}

export interface BornesCurseur {
  min: number;
  max: number;
  pas: number;
}

const MACROS_ZERO: MacrosPer100g = {
  kcal: 0,
  glucides_g: 0,
  proteines_g: 0,
  lipides_g: 0,
  fibres_g: 0,
  sucres_g: 0,
  sel_g: 0,
};

/** Poids de départ d'un aliment : le milieu de la fourchette, arrondi au gramme. */
export function poidsInitial(estimation: PoidsEstime): number {
  return Math.round((estimation.poids_g_min + estimation.poids_g_max) / 2);
}

/**
 * Bornes du curseur. Volontairement PLUS LARGES que la fourchette du modèle : sa couverture mesurée
 * est de 52 % brute (eval/README.md), donc une fois sur deux le vrai poids est hors fourchette — un
 * curseur qui s'arrêterait à `poids_g_max` empêcherait de corriger précisément le cas le plus
 * fréquent, la sous-estimation des grosses portions.
 */
export function bornesCurseur(aliment: AlimentAjustable): BornesCurseur {
  const estimation = aliment.estimation;
  const centre = estimation ? poidsInitial(estimation) : aliment.poidsG;
  const bas = estimation ? estimation.poids_g_min : centre;
  const haut = estimation ? estimation.poids_g_max : centre;
  const pas = haut > 300 || aliment.poidsG > 300 ? 10 : 5;
  const min = Math.max(pas, arrondirAuPas(bas * 0.5, pas, 'bas'));
  const max = Math.max(min + pas * 4, arrondirAuPas(Math.max(haut * 2, aliment.poidsG * 1.5, 100), pas, 'haut'));
  return { min, max, pas };
}

function arrondirAuPas(valeur: number, pas: number, sens: 'bas' | 'haut'): number {
  const div = valeur / pas;
  return (sens === 'bas' ? Math.floor(div) : Math.ceil(div)) * pas;
}

/** Convertit un évènement `aliment` du flux SSE en ligne ajustable. */
export function alimentDepuisDetection(detecte: AlimentDetecte, id: string): AlimentAjustable {
  return {
    id,
    nom: detecte.nom,
    confiance: detecte.confiance,
    origine: 'modele',
    estimation: detecte.poids,
    macrosPour100g: detecte.macrosPour100g,
    poidsG: poidsInitial(detecte.poids),
    ajuste: false,
  };
}

export function alimentManuel(
  id: string,
  nom: string,
  poidsG: number,
  macrosPour100g: MacrosPer100g | null,
): AlimentAjustable {
  return {
    id,
    nom,
    confiance: null,
    origine: 'manuel',
    estimation: null,
    macrosPour100g,
    poidsG: Math.max(1, Math.round(poidsG)),
    ajuste: false,
  };
}

/** Macros de l'aliment au poids courant — `null` si sa composition est inconnue. */
export function macrosCourantes(aliment: AlimentAjustable): MacrosPer100g | null {
  if (!aliment.macrosPour100g) return null;
  return scaleMacros(aliment.macrosPour100g, aliment.poidsG);
}

export function confianceBasse(aliment: AlimentAjustable): boolean {
  return aliment.confiance !== null && aliment.confiance < SEUIL_CONFIANCE_BASSE;
}

export interface TotauxPlat {
  poidsG: number;
  macros: MacrosPer100g;
  /** Aliments dont la composition est inconnue : comptés dans le poids, pas dans les macros. */
  alimentsSansComposition: number;
}

/** Somme des lignes au poids courant. Recalculée à chaque mouvement de curseur, sans réseau. */
export function totauxPlat(aliments: readonly AlimentAjustable[]): TotauxPlat {
  const cumul: MacrosPer100g = { ...MACROS_ZERO };
  let poidsG = 0;
  let alimentsSansComposition = 0;

  for (const aliment of aliments) {
    poidsG += aliment.poidsG;
    const macros = macrosCourantes(aliment);
    if (!macros) {
      alimentsSansComposition++;
      continue;
    }
    cumul.kcal += macros.kcal;
    cumul.glucides_g += macros.glucides_g;
    cumul.proteines_g += macros.proteines_g;
    cumul.lipides_g += macros.lipides_g;
    cumul.fibres_g += macros.fibres_g;
    cumul.sucres_g += macros.sucres_g;
    cumul.sel_g += macros.sel_g;
  }

  return {
    poidsG: Math.round(poidsG),
    macros: {
      kcal: Math.round(cumul.kcal),
      glucides_g: arrondi(cumul.glucides_g, 1),
      proteines_g: arrondi(cumul.proteines_g, 1),
      lipides_g: arrondi(cumul.lipides_g, 1),
      fibres_g: arrondi(cumul.fibres_g, 1),
      sucres_g: arrondi(cumul.sucres_g, 1),
      sel_g: arrondi(cumul.sel_g, 2),
    },
    alimentsSansComposition,
  };
}

/**
 * Fourchette de poids du plat telle que le modèle l'a rendue : somme des `poids_g_min` / `poids_g_max`
 * des lignes NON ajustées, plus le poids retenu pour celles que l'utilisateur a corrigées (sur
 * celles-là, il n'y a plus d'incertitude à afficher). `null` si plus aucune ligne ne vient du modèle.
 */
export function fourchettePlat(aliments: readonly AlimentAjustable[]): PoidsEstime | null {
  let min = 0;
  let max = 0;
  let auMoinsUn = false;
  for (const aliment of aliments) {
    if (aliment.estimation && !aliment.ajuste) {
      min += aliment.estimation.poids_g_min;
      max += aliment.estimation.poids_g_max;
      auMoinsUn = true;
    } else {
      min += aliment.poidsG;
      max += aliment.poidsG;
    }
  }
  return auMoinsUn ? { poids_g_min: Math.round(min), poids_g_max: Math.round(max) } : null;
}

// --- Opérations sur la liste (immuables : une nouvelle liste à chaque fois, le rendu compare) ---

export function ajusterPoids(
  aliments: readonly AlimentAjustable[],
  id: string,
  poidsG: number,
): AlimentAjustable[] {
  return aliments.map((aliment) =>
    aliment.id === id ? { ...aliment, poidsG: Math.max(1, Math.round(poidsG)), ajuste: true } : aliment,
  );
}

export function renommer(aliments: readonly AlimentAjustable[], id: string, nom: string): AlimentAjustable[] {
  const propre = nom.trim();
  if (!propre) return [...aliments];
  return aliments.map((aliment) => (aliment.id === id ? { ...aliment, nom: propre } : aliment));
}

export function supprimer(aliments: readonly AlimentAjustable[], id: string): AlimentAjustable[] {
  return aliments.filter((aliment) => aliment.id !== id);
}

export function ajouter(
  aliments: readonly AlimentAjustable[],
  aliment: AlimentAjustable,
): AlimentAjustable[] {
  return [...aliments, aliment];
}

function arrondi(valeur: number, decimales: number): number {
  const facteur = 10 ** decimales;
  return Math.round(valeur * facteur) / facteur;
}
